import { Grid, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { appHeaderTitle } from "../../common/services/Utilities";
import { AppUrl } from "../../config";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { AppBody } from "../AppBody";
import { useEffect, useState } from "react";
import SummaryTablesStatic from "../../common/assets/images/SOD_Summary_Static.svg";
import SummaryTablesHover from "../../common/assets/images/SOD_Summary_Focus.svg";
import MarketShareStatic from "../../common/assets/images/SOD_MarketShare_Static.svg";
import MarketShareHover from "../../common/assets/images/SOD_MarketShare_Focus.svg";
import BODStatic from "../../common/assets/images/SOD_BOD_Static.svg";
import BODHover from "../../common/assets/images/SOD_BOD_Focus.svg";
import CustomDownloadStatic from "../../common/assets/images/SOD_Download_Static.svg";
import CustomDownloadHover from "../../common/assets/images/SOD_Download_Focus.svg";
import ManagePGStatic from "../../common/assets/images/SavePeersReports_Static.svg";
import ManagePGHover from "../../common/assets/images/SavePeersReports_foucs.svg";
import { LevelTwoAppCard, LevelTwoAppCardProps } from "../LevelTwoAppCard";

interface Props {
  resetState: () => void;
}
export default function SOD({ resetState }: Props) {
  const navigate = useNavigate();
  useInitialPageLoad("SOD");
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (initialLoad) {
      resetState();
      setInitialLoad(false);
    }
  }, [initialLoad, resetState]);

  const introParagraph = (
    <>
      {appHeaderTitle("SOD")}
      <Typography variant="body2" id="peer-group-description">
        The annual Summary of Deposits (SOD) survey data provides a number of
        opportunities to better understand the status of our banking system.
        With the holistic aggregations of the Summary Tables, the geographically
        sensitive Market Share Reports, and the institution-centric Branch
        Office Deposits, you will be able to see a clear view of where the
        deposits are and how they’ve changed over time.
      </Typography>
      <Typography variant="body2" id="peer-group-data-amendments">
        The results of the SOD survey are ‘as reported’ by the submitting
        institutions. Occasionally there are amendments to the survey.
        Amendments may include address corrections, county code changes, revised
        zip codes and deposit amounts. Please see our{" "}
        <Link
          href={
            "/bankfind-suite/help?helpTopic=summary-of-deposits-data-amendments"
          }
        >
          Help Library
        </Link>{" "}
        for a list of past SOD data amendments dates.
      </Typography>
      <Typography variant="body2" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=SOD"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  const sodApps: LevelTwoAppCardProps[] = [
    {
      id: "summaryTables",
      iconStatic: SummaryTablesStatic,
      iconHover: SummaryTablesHover,
      title: <>Summary Tables</>,
      alt: "Summary Tables",
      onClick: () =>
        navigate(
          {
            pathname: `${AppUrl("SOD")}/summaryTables`,
          },
          {
            state: { url: `${AppUrl("SOD")}` },
          },
        ),
    },
    {
      id: "marketShare",
      iconStatic: MarketShareStatic,
      iconHover: MarketShareHover,
      title: <>Market Share Reports</>,
      alt: "Market Share Reports",
      onClick: () =>
        navigate(
          {
            pathname: `${AppUrl("SOD")}/marketShare`,
          },
          {
            state: { url: `${AppUrl("SOD")}` },
          },
        ),
    },
    {
      id: "branchOfficeDeposits",
      iconStatic: BODStatic,
      iconHover: BODHover,
      title: <>Branch Office Deposits</>,
      alt: "Branch Office Deposits",
      onClick: () =>
        navigate(
          {
            pathname: `${AppUrl("SOD")}/branchOffice`,
          },
          {
            state: { url: `${AppUrl("SOD")}` },
          },
        ),
    },
    {
      id: "sodCustomDataDownload",
      iconStatic: CustomDownloadStatic,
      iconHover: CustomDownloadHover,
      title: <>Custom Data Download</>,
      alt: "Custom Data Download",
      onClick: () =>
        navigate(
          {
            pathname: `${AppUrl("SOD")}/customDownload`,
            search: "?customDataDownload=true",
          },
          {
            state: { url: `${AppUrl("SOD")}` },
          },
        ),
    },
    {
      id: "sodManageSavedContent",
      iconStatic: ManagePGStatic,
      iconHover: ManagePGHover,
      title: (
        <>
          Manage Your Saved
          <br />
          Custom Content
        </>
      ),
      alt: "Manage Your Saved Custom Content",
      onClick: () =>
        navigate(
          {
            pathname: `${AppUrl("PeerGroup")}/manage`,
            search: "?tab=Custom SOD Downloads",
          },
          {
            state: {
              url: AppUrl("SOD") + window.location.search,
            },
          },
        ),
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Summary of Deposits" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody introText={introParagraph}>
        <Grid container sx={{ py: 3, maxWidth: 1200, mx: "auto" }} spacing={2}>
          {sodApps.map((app) => (
            <Grid
              key={app.id}
              size={{
                xs: 12,
                md: 3,
              }}
              sx={{ height: 230 }}
            >
              <LevelTwoAppCard {...app} />
            </Grid>
          ))}
        </Grid>
      </AppBody>
    </>
  );
}
